import type { AxiosRequestConfig } from 'axios';
import { isBrowser } from './browserHelper';
import { getAppViewAccessToken } from './cookieHelper';
import { BOKA_QUERY_PARAMS } from './urlHelper';
import services from '../services';
import type { ConfirmAppointmentParams } from '../services/appointment/model';
import type { AppView } from '../types/app';

const applyBearerToken = (token: string): AxiosRequestConfig => ({
  headers: { Authorization: `Bearer ${token}` },
});

const isLocalEnv = TARGET === 'local';

const authTokenHandler = () => {
  const buildAuthConfig = async (
    view: AppView,
  ): Promise<AxiosRequestConfig> => {
    if (!isBrowser()) return {};

    if (view === 'app') {
      const appViewAccessToken = getAppViewAccessToken();
      if (!appViewAccessToken) {
        const testMode = new URLSearchParams(window.location.search).get(
          BOKA_QUERY_PARAMS.TEST_MODE,
        );
        if (testMode === 'true') return {};
        throw new Error('Token not found');
      }
      return applyBearerToken(appViewAccessToken);
    }

    const isLoggedIn = window?.ikea?.authentication?.isLoggedIn;
    if (!isLoggedIn) return {};
    const payload = await isLoggedIn();
    if (!payload.isLoggedIn) return {};
    return applyBearerToken(payload.authToken);
  };

  const getUserInformation = async (view: AppView) => {
    const authConfig = isLocalEnv
      ? applyBearerToken('test-token')
      : await buildAuthConfig(view);
    return services.user.getUserInformation(authConfig);
  };

  const confirmAppointment = async (
    params: ConfirmAppointmentParams,
    view: AppView,
  ) => {
    const authConfig = isLocalEnv ? {} : await buildAuthConfig(view);
    return services.appointment.confirmAppointment(params, authConfig);
  };

  return { getUserInformation, confirmAppointment };
};

export const authTokenHandlerInstance = authTokenHandler();
